import "./Athlete.css";
import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useModel, usePortal } from "../context.js";
import { errorMessage, historyKey, loadHistory, loadKey, loadLoad, loadMeets, meetsKey, useResource } from "../data.js";
import { chartGeometry } from "../model/chart.js";
import { addDays, dayLong, dayShort, mondayOf, monthDay, parseDay, weekRange } from "../model/days.js";
import { averageText, bodyText, effortLoadText, effortWord, hasEffort, recentReports, weeklyEffort, type WeekEffort } from "../model/effort.js";
import { signed } from "../model/lifts.js";
import { type LoadStatus, loadNumber, loadStatus, trendText } from "../model/load.js";
import { keyOf, liftTarget, norm, plural, working } from "../model/plan.js";
import { progress, type LiftProgress, type ProgressPoint } from "../model/progress.js";
import { usePrefs } from "../prefs.js";
import { raceTime, recordText, setText, weightNumber } from "../model/sets.js";
import { hasRuns, lastMondays, metresByTypeText, metresOrdered, metresStack, metresText, RUN_TYPE_LABEL, RUN_TYPES, type RunType, runBests, runDistanceText, speedText, weeklyMetres, type RunBest } from "../model/running.js";
import type { HistoryWorkout, Meet } from "../model/types.js";
import { athleteDays, type AthleteDay, type Athlete as AthleteModel, type State } from "../model/week.js";
import { href } from "../paths.js";
import { Link, useNavigate } from "react-router";
import { type Column, PageHeader, PbMark, Status, Table } from "../../kit/index.js";
import { pinnedFor } from "../model/notes.js";
import { Notes, useNotes } from "./Notes.js";
import { BodyLevel, EffortMarks } from "./common.js";
import { isOn, lastDay, meetsFor, meetsOn, meetWhen, meetWhenLong, nextMeetFor } from "../model/meets.js";
import { MeetLink, meetPath } from "./MeetMarks.js";
import { useMeets } from "./Meets.js";
import { Sprints } from "./Sprints.js";

export function columnText(a: AthleteModel, long = false): string {
  return a.column ? a.column : a.nothing ? "Nothing this week" : a.noGroup ? (long ? "No group, so everyone's sessions" : "No group") : "";
}

export function weekText(a: AthleteModel): string {
  return a.total ? `${a.done} of ${a.total} done` + (a.skipped ? ` · ${a.skipped} skipped` : "") : plural(a.workouts.length, "workout", "workouts");
}

/**
 * An athlete's own page (DESIGN §16.3, §16.6): about them, not one week. Their load over twelve
 * weeks and this week's sessions beside their running, then their lifts over everything shared,
 * and every workout. Esc goes back to the Athletes table.
 */
export function AthletePage({ id, backTo }: { id: string; backTo: string }) {
  const model = useModel();
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if ((e.target as Element | null)?.closest?.("input, textarea, select, [role=dialog], [role=menu]")) return;
      navigate(backTo);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [backTo, navigate]);
  const back = (
    <Link className="btn ath-back" to={backTo}>
      <span aria-hidden="true">‹</span> Athletes
    </Link>
  );
  const a = model.athletes.find((x) => x.id === id);
  const pinned = pinnedFor(useNotes(), id);
  if (!a)
    return (
      <div className="k-page">
        <PageHeader lead={back} title="Athlete not found" context="They may have left the team." />
      </div>
    );
  const column = columnText(a, true);
  const last = a.lastWorkout ? "Last trained " + dayShort(a.lastWorkout) : "No workouts shared yet";
  return (
    <div className="k-page ath-page">
      <header className="k-header ath-head">
        <div className="ath-head-top">
          {back}
          <span className="ath-avatar" aria-hidden="true">
            {initials(a.name)}
          </span>
          <div className="ath-head-title">
            <h1>{a.name}</h1>
            <div className="ath-head-meta">
              {column ? <span className="ath-pill">{column}</span> : null}
              <span>{last}</span>
              <NextMeet athleteID={a.id} />
            </div>
          </div>
        </div>
        {pinned ? (
          <a className="ath-pin" href="#ath-notes" onClick={jump("ath-notes")}>
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path fill="currentColor" d="M4.2 1h3.6l-.5 3.1 1.9 1.7v1.1H6.6V11L6 11.6 5.4 11V6.9H2.8V5.8l1.9-1.7L4.2 1Z" />
            </svg>
            <span className="ath-pin-label">
              <span className="sr">Pinned note: </span>
              <span aria-hidden="true">Pinned</span>
            </span>
            <span className="ath-pin-text">{pinned.body}</span>
          </a>
        ) : null}
      </header>
      <Summary athlete={a} />
      <div className="ath-layout">
        <div className="ath-aside">
          <Notes key={"notes:" + a.id} athleteID={a.id} name={a.name} />
        </div>
        <div className="ath-main">
          <ThisWeek athlete={a} />
          <AthleteMeets athleteID={a.id} />
          <LoadSection athleteID={a.id} />
          <Running athleteID={a.id} />
          <Sprints athleteID={a.id} />
          <Effort athleteID={a.id} />
          <Progress athlete={a} />
          <History athleteID={a.id} />
        </div>
      </div>
    </div>
  );
}

function daysAway(from: string, to: string): number {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86_400_000);
}

/** The next meet they are going to (§27.3): nothing when there is none or the meets can't be read. */
function NextMeet({ athleteID }: { athleteID: string }) {
  const model = useModel();
  const until = addDays(model.today, 365);
  const meets = useResource(meetsKey(model.today, until), () => loadMeets(model.today, until)).value;
  const next = nextMeetFor(meets, athleteID, model.today);
  if (!next) return null;
  const n = daysAway(model.today, next.date);
  const when = n <= 0 ? "today" : n === 1 ? "tomorrow" : "in " + n + " days";
  return (
    <span className="ath-meet">
      <svg viewBox="0 0 12 12" aria-hidden="true">
        <path fill="currentColor" d="M2 1h1.1v10H2V1Zm1.6.4h7.4v5.4H3.6V1.4Zm1.1.9v1.6h1.5V2.3H4.7Zm3 0v1.6h1.5V2.3H7.7ZM6.2 3.9v1.6h1.5V3.9H6.2Zm-1.5 1.6H3.6v.4h1.1v-.4Zm4.5-1.6v1.6h.9V3.9h-.9Z" />
      </svg>
      <span>
        Next meet: <b>{next.name}</b> · {dayShort(next.date)} · {when}
      </span>
    </span>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

/** A heading row (title, a few words of context, its controls) over one card: every section of the page. */
function Section({
  id,
  hid,
  title,
  note,
  tools,
  flush,
  children,
}: {
  id?: string;
  hid: string;
  title: string;
  note?: ReactNode;
  tools?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="ath-section" id={id} aria-labelledby={hid}>
      <div className="ath-sec-head">
        <h2 id={hid}>{title}</h2>
        {note ? <span className="ath-sec-note num">{note}</span> : null}
        {tools ? <div className="ath-sec-tools">{tools}</div> : null}
      </div>
      <div className={"ath-card" + (flush ? " is-flush" : "")}>{children}</div>
    </section>
  );
}

function jump(id: string) {
  return (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ block: "start", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };
}

/** Their week at a glance; each tile goes to its section. */
function Summary({ athlete: a }: { athlete: AthleteModel }) {
  const model = useModel();
  const load = useResource(loadKey, loadLoad);
  const history = useResource(historyKey(a.id), () => loadHistory(a.id));
  const l = load.value ? loadOf(load.value, a.id, model.today) : null;
  const workouts = history.value?.workouts ?? [];
  const running = hasRuns(workouts) ? weeklyMetres(workouts, lastMondays(model.today)).at(-1)! : null;
  const tiles: { to: string; label: string; value: string; sub: ReactNode }[] = [
    {
      to: "ath-week",
      label: "This week",
      value: a.total ? `${a.done} of ${a.total}` : String(a.workouts.length),
      sub: a.total ? ["done", a.skipped ? `${a.skipped} skipped` : null, a.missed ? `${a.missed} not logged` : null].filter(Boolean).join(" · ") : a.workouts.length === 1 ? "workout" : "workouts",
    },
    {
      to: "ath-load",
      label: "Load",
      value: l ? loadNumber(l.lead.value) : "—",
      sub: l ? <>{l.lastFull ? "last week · " : "this week · "}{trendMark(l.lead)}</> : load.error ? "Couldn't load" : load.value ? "No weighed sets yet" : "…",
    },
  ];
  if (running) tiles.push({ to: "ath-running", label: "Running", value: metresText(running.total), sub: "this week" });
  tiles.push({ to: "ath-week", label: "Personal bests", value: String(a.prs), sub: "this week" });
  return (
    <nav className="ath-summary" aria-label="At a glance">
      {tiles.map((t) => (
        <a key={t.label} className="ath-tile" href={"#" + t.to} onClick={jump(t.to)}>
          <span className="ath-tile-label">{t.label}</span>
          <span className="ath-tile-value num">{t.value}</span>
          <span className="ath-tile-sub">{t.sub}</span>
        </a>
      ))}
    </nav>
  );
}

function Notice({ children, alert, busy }: { children: ReactNode; alert?: boolean; busy?: boolean }) {
  return (
    <p className="ath-notice" role={alert ? "alert" : busy ? "status" : undefined}>
      {children}
    </p>
  );
}

function trendMark(s: LoadStatus) {
  const kind = s.trend === "high" ? "high" : s.trend === "low" ? "low" : "usual";
  return <Status kind={kind} text={trendText(s)} />;
}

type LoadData = Awaited<ReturnType<typeof loadLoad>>;

/** Their row of the team's load, and how this week and the last full one read against their usual. */
function loadOf(data: LoadData, athleteID: string, today: string) {
  const { weeks } = data;
  const athlete = data.athletes.find((x) => x.id === athleteID);
  if (!athlete || !weeks.length) return null;
  const ongoing = weeks[weeks.length - 1] === mondayOf(today);
  const status = loadStatus(weeks, athlete, weeks.length - 1, ongoing);
  // A week still running is a partial number: the last full week leads, this week sits beside it.
  const lastFull = ongoing && weeks.length > 1 ? loadStatus(weeks, athlete, weeks.length - 2) : null;
  return { weeks, athlete, ongoing, status, lastFull, lead: lastFull ?? status };
}

/** Their last twelve weeks of load, this week last, read against their own usual. */
function LoadSection({ athleteID }: { athleteID: string }) {
  const data = useResource(loadKey, loadLoad);
  const model = useModel();
  const l = data.value ? loadOf(data.value, athleteID, model.today) : null;
  const section = (children: ReactNode, tools?: ReactNode) => (
    <Section id="ath-load" hid="ath-load-h" title="Load" note="Last 12 weeks" tools={tools}>
      {children}
    </Section>
  );
  if (data.error) return section(<Notice alert>{errorMessage(data.error)}</Notice>);
  if (!data.value) return section(<Notice busy>Loading their load…</Notice>);
  if (!l) return section(<Notice>No weighed sets shared yet.</Notice>);
  const { weeks, athlete, ongoing, status, lastFull, lead } = l;
  const max = Math.max(...athlete.loads, status.usual ?? 0, 1);
  const W = 600;
  const H = 96;
  const gap = 6;
  const bar = (W - gap * (weeks.length - 1)) / weeks.length;
  const y = (v: number) => H - (v / max) * (H - 4);
  return section(
    <>
      <div className="ath-chart-keys" aria-hidden="true">
        <span className="ath-key">
          <span className="ath-key-swatch is-lead" />
          {lastFull ? "Last week" : "This week"} <b className="num">{loadNumber(lead.value)}</b>
        </span>
        {lastFull ? (
          <span className="ath-key">
            <span className="ath-key-swatch is-current" />
            This week so far <b className="num">{loadNumber(status.value)}</b>
          </span>
        ) : null}
        {status.usual != null ? (
          <span className="ath-key">
            <span className="ath-key-line" />
            Usual <b className="num">{loadNumber(status.usual)}</b>
          </span>
        ) : null}
      </div>
      <WeekBars
        count={weeks.length}
        width={W}
        height={H}
        center={(i) => i * (bar + gap) + bar / 2}
        tip={(i) => {
          const v = athlete.loads[i] ?? 0;
          const current = ongoing && i === weeks.length - 1;
          return {
            top: current && v === 0 ? H - 4 : y(v),
            body: (
              <>
                <b className="num">{loadNumber(v)}</b>
                <span>{current ? "This week so far" : "Week of " + monthDay(weeks[i]!)}</span>
              </>
            ),
          };
        }}
      >
        {(active) => (
          <>
            {athlete.loads.map((v, i) => {
              const last = i === weeks.length - 1;
              const on = i === active ? " is-active" : "";
              if (last && ongoing && v === 0) return <rect key={weeks[i]} className={"ath-bar is-pending" + on} x={i * (bar + gap)} y={H - 4} width={bar} height={4} rx={2} />;
              const tone = i === weeks.length - (lastFull ? 2 : 1) ? " is-lead" : last ? " is-current" : "";
              return <rect key={weeks[i]} className={"ath-bar" + tone + on} x={i * (bar + gap)} y={y(v)} width={bar} height={Math.max(H - y(v), v > 0 ? 2 : 0)} rx={3} />;
            })}
            {status.usual != null ? <line className="ath-usual" x1={0} x2={W} y1={y(status.usual)} y2={y(status.usual)} /> : null}
          </>
        )}
      </WeekBars>
      <div className="ath-axis" aria-hidden="true">
        <span>{monthDay(weeks[0]!)}</span>
        <span>{ongoing ? "This week" : monthDay(weeks[weeks.length - 1]!)}</span>
      </div>
      <div className="sr">
        <table>
          <caption>Load by week</caption>
          <thead>
            <tr>
              <th scope="col">Week of</th>
              <th scope="col">Load</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w, i) => (
              <tr key={w}>
                <td>{monthDay(w)}</td>
                <td>{loadNumber(athlete.loads[i] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LoadExplainer usual={lead.usual} />
    </>,
    trendMark(lead),
  );
}

/** What the number is, in a coach's words, with one set worked through. */
export function LoadExplainer({ usual }: { usual?: number | null }) {
  return (
    <details className="ath-explainer">
      <summary>How load is counted</summary>
      <div className="ath-explainer-body">
        <p>
          <b>Each set</b> counts its reps × how close the weight was to their best for that lift. Their best is what
          they could lift once, estimated from their heaviest sets so far (weight × (1 + reps ÷ 30)).
        </p>
        <p className="ath-example">
          Best back squat 125 kg; a set of 5 at 100 kg is 80% of it, so it counts <b>5 × 0.8 = 4</b>. The same 5 reps at
          60 kg count 2.4.
        </p>
        <p>
          <b>A week</b> adds up every set, Monday to Sunday. Heavy sets weigh more than light ones, so a few hard sets
          can match many easy ones, and athletes of different strength compare fairly.
        </p>
        <p>
          <b>Their usual</b> is the average of the four weeks before{usual != null ? ` (${loadNumber(usual)} here, the dashed line)` : ""}. A week 30%
          or more above it is <b>well above</b>; 30% or more below is <b>well below</b>. A week still under way is never
          called low.
        </p>
        <p className="k-muted">
          Weights only: sets with a weight and reps. Bodyweight, timed and distance work, sprints and running aren't
          counted.
        </p>
      </div>
    </details>
  );
}

/** Their days this week, a day they race on among them even with nothing else on it (§27.3.1). */
function weekDaysWithMeets(days: AthleteDay[], meets: Meet[], week: string[]): AthleteDay[] {
  const have = new Set(days.map((d) => d.date));
  const raceDays = week.filter((d) => !have.has(d) && meets.some((m) => isOn(m, d)));
  if (!raceDays.length) return days;
  return [...days, ...raceDays.map((date) => ({ date, parts: [], state: null, records: 0, summary: [] }))].sort((x, y) => x.date.localeCompare(y.date));
}

const STATE_MARK: Record<State, string> = { done: "✓", skipped: "⊖", missed: "–", today: "●", planned: "○" };
const STATE_WORD: Record<State, string> = { done: "Done", skipped: "Skipped", missed: "Not logged", today: "Today", planned: "Planned" };

function ThisWeek({ athlete: a }: { athlete: AthleteModel }) {
  const model = useModel();
  const read = useMeets(model.monday, model.sunday);
  const meets = useMemo(() => meetsFor(a.id, read), [a.id, read]);
  const days = weekDaysWithMeets(athleteDays(model, a), meets, model.days);
  const columns: Column<AthleteDay>[] = [
    { key: "day", label: "Day", render: (d) => dayShort(d.date) },
    {
      key: "sessions",
      label: "Sessions",
      wrap: true,
      render: (d) => {
        // What was asked of them a line each, its state the mark before it; the rest of the plan one quiet line.
        const owed = d.parts.filter((p) => p.weights || p.state);
        const rest = d.parts.filter((p) => !(p.weights || p.state));
        return (
          <span className="ath-lines">
            {meetsOn(meets, d.date).map((m) => (
              <MeetLink key={m.id} meet={m} className="ath-race" />
            ))}
            {owed.map((p, i) => {
              const state = p.weights ? d.state : p.state;
              return (
                <span key={i} className={"ath-owed" + (state ? " is-" + state : "")}>
                  {state ? <span aria-hidden="true">{STATE_MARK[state]} </span> : null}
                  {p.name}
                  {state ? <span className="sr">, {STATE_WORD[state]}</span> : null}
                  {state === "done" && p.skipped ? <span className="ath-skipped"> · {p.skipped} skipped</span> : null}
                </span>
              );
            })}
            {rest.length ? (
              <span className="ath-rest">
                {rest.map((p, i) => (
                  <span key={i}>
                    {i ? <span aria-hidden="true"> · </span> : null}
                    {p.name}
                    {p.logged ? <span aria-hidden="true"> ✓</span> : null}
                    {p.logged ? <span className="sr">, logged</span> : null}
                    {!p.logged && p.skipped ? <span aria-hidden="true"> ⊖</span> : null}
                    {!p.logged && p.skipped ? <span className="sr">, skipped</span> : null}
                  </span>
                ))}
              </span>
            ) : null}
          </span>
        );
      },
    },
    { key: "top", label: "Top sets", wrap: true, render: (d) => (d.summary.length ? <span className="ath-top num">{d.summary.join(" · ")}</span> : null) },
    { key: "pb", label: "Personal bests", hideLabel: true, align: "end", render: (d) => <PbMark n={d.records} /> },
  ];
  if (hasEffort(a.workouts))
    columns.splice(3, 0, {
      key: "felt",
      label: "How it felt",
      wrap: true,
      render: (d) => (
        <span className="ath-felt">
          {a.workouts
            .filter((w) => w.date === d.date)
            .map((w, i) => (
              <EffortMarks key={i} session={w.session} />
            ))}
        </span>
      ),
    });
  return (
    <Section id="ath-week" hid="ath-week-h" title="This week" note={weekRange(model.monday)} flush>
      <Table rows={days} columns={columns} rowKey={(d) => d.date} href={(d) => href(model.monday, "day", d.date)} label={`${a.name}, this week`} empty="Nothing given or shared this week." />
    </Section>
  );
}

/** Their meets from eight weeks back to a year ahead (§27.3.1), read as two ranges: the Worker reads at most a year at once. */
function AthleteMeets({ athleteID }: { athleteID: string }) {
  const model = useModel();
  const past = useMeets(addDays(model.today, -56), addDays(model.today, -1));
  const coming = useMeets(model.today, addDays(model.today, 365));
  const meets = useMemo(() => meetsFor(athleteID, past, coming), [athleteID, past, coming]);
  if (!meets.length) return null;
  const isPast = (m: Meet) => lastDay(m) < model.today;
  const columns: Column<Meet>[] = [
    {
      key: "date",
      label: "Date",
      render: (m) => (
        <span className="num">
          <span aria-hidden="true">{meetWhen(m)}</span>
          <span className="sr">{meetWhenLong(m)}</span>
        </span>
      ),
    },
    {
      key: "name",
      label: "Meet",
      wrap: true,
      render: (m) => (
        <span className="ath-meet-name">
          {m.name}
          {isPast(m) ? <span className="ath-past">past</span> : null}
        </span>
      ),
    },
    { key: "place", label: "Place", wrap: true, render: (m) => m.place || <span className="k-muted">—</span> },
  ];
  return (
    <Section id="ath-meets" hid="ath-meets-h" title="Meets" note="8 weeks back to a year ahead" flush>
      <Table rows={meets} columns={columns} rowKey={(m) => m.id} href={(m) => meetPath(m.id)} rowClass={(m) => (isPast(m) ? "is-past" : undefined)} label={"Meets"} />
    </Section>
  );
}

/** Everything they have shared, newest first; each opens to its sets. */
function History({ athleteID }: { athleteID: string }) {
  const history = useResource(historyKey(athleteID), () => loadHistory(athleteID));
  const [shown, setShown] = useState(12);
  const [opened, setOpened] = useState<string | null>(null);
  const workouts = history.value?.workouts ?? [];
  useEffect(() => {
    const onOpen = (e: Event) => {
      const date = (e as CustomEvent<string>).detail;
      const i = workouts.findIndex((w) => w.date === date);
      if (i < 0) return;
      setShown((n) => Math.max(n, i + 1));
      setOpened(date + ":" + Date.now());
    };
    window.addEventListener(OPEN_WORKOUT, onOpen);
    return () => window.removeEventListener(OPEN_WORKOUT, onOpen);
  }, [workouts]);
  // Arriving from a link to one workout (Attention's flags): `#ath-w-<date>` opens it once History has it.
  const arrived = useRef(false);
  useEffect(() => {
    if (arrived.current || !workouts.length) return;
    const date = /^#ath-w-(\d{4}-\d{2}-\d{2})$/.exec(window.location.hash)?.[1];
    if (!date) return;
    arrived.current = true;
    const i = workouts.findIndex((w) => w.date === date);
    if (i < 0) return;
    setShown((n) => Math.max(n, i + 1));
    setOpened(date + ":" + Date.now());
  }, [workouts]);
  useEffect(() => {
    if (!opened) return;
    const el = document.getElementById("ath-w-" + opened.split(":")[0]);
    if (el instanceof HTMLDetailsElement) {
      el.open = true;
      el.scrollIntoView({ block: "start", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      el.querySelector("summary")?.focus({ preventScroll: true });
    }
  }, [opened]);
  const groups: { week: string; items: { w: HistoryWorkout; i: number }[] }[] = [];
  workouts.slice(0, shown).forEach((w, i) => {
    const week = mondayOf(w.date);
    if (groups.at(-1)?.week !== week) groups.push({ week, items: [] });
    groups.at(-1)!.items.push({ w, i });
  });
  const count = history.value ? plural(workouts.length, "workout", "workouts") + " shared" : undefined;
  return (
    <Section id="ath-history" hid="ath-history-h" title="History" note={count} flush>
      {history.error ? (
        <Notice alert>{errorMessage(history.error)}</Notice>
      ) : !history.value ? (
        <Notice busy>Loading their workouts…</Notice>
      ) : !workouts.length ? (
        <Notice>No workouts shared yet.</Notice>
      ) : (
        <>
          <div className="ath-history">
            {groups.map((g) => (
              <div key={g.week} className="ath-history-week" role="group" aria-label={"Week of " + monthDay(g.week)}>
                <div className="ath-history-week-h" aria-hidden="true">
                  Week of {monthDay(g.week)}
                </div>
                {g.items.map(({ w, i }) => {
                  const sets = (w.session?.logs ?? []).length;
                  return (
                    <details
                      key={w.date + ":" + i}
                      id={workouts.findIndex((x) => x.date === w.date) === i ? "ath-w-" + w.date : undefined}
                      className={"ath-history-row" + (sets ? "" : " is-empty")}
                    >
                      <summary>
                        <svg className="ath-disclosure" viewBox="0 0 8 12" aria-hidden="true">
                          <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="m2 2 4 4-4 4" />
                        </svg>
                        <span className="ath-history-date">{dayLong(w.date)}</span>
                        <span className="ath-history-sum">{historySummary(w)}</span>
                        <EffortMarks session={w.session} />
                        <PbMark n={w.records?.length ?? 0} />
                      </summary>
                      <WorkoutSets w={w} />
                    </details>
                  );
                })}
              </div>
            ))}
          </div>
          {workouts.length > shown ? (
            <div className="ath-card-foot">
              <button type="button" className="note-link" onClick={() => setShown((n) => n + 24)}>
                Show older workouts
              </button>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

function historySummary(w: HistoryWorkout): string {
  const s = w.session ?? {};
  const sets = (s.logs ?? []).filter((l) => !l.skipped).length;
  const skipped = (s.logs ?? []).length - sets;
  const title = (w.title || s.title || "").trim();
  // A workout the phone named after its day says nothing the date doesn't.
  const named = title && !/^(mon|tues|wednes|thurs|fri|satur|sun)day\b/i.test(title) ? title : "";
  return [named, sets ? plural(sets, "set", "sets") : skipped ? "Skipped" : "Nothing logged", sets && skipped ? `${skipped} skipped` : null].filter(Boolean).join(" · ");
}

export function WorkoutSets({ w }: { w: HistoryWorkout }) {
  const s = w.session ?? {};
  const records = new Map<string, string>();
  for (const r of w.records ?? []) records.set(norm(r.logID), r.label);
  const exNotes = new Map<string, string[]>();
  for (const n of s.exerciseNotes ?? []) exNotes.set(n.exerciseKey, [...(exNotes.get(n.exerciseKey) ?? []), n.text]);
  const lifts = working(s.blocks).flatMap((b, bi) =>
    (b.exercises ?? []).map((e, ei) => {
      const logs = (s.logs ?? []).filter((l) => norm(l.exerciseID) === norm(e.id)).sort((x, y) => (x.setIndex ?? 0) - (y.setIndex ?? 0));
      const target = liftTarget(e, b);
      return (
        <div className="ath-lift" key={bi + ":" + ei}>
          <table className="ath-sets">
            <caption>
              <b>{e.name}</b>
              {target ? <span className="k-muted num"> {target}</span> : null}
            </caption>
            <thead className="sr">
              <tr>
                <th scope="col">Set</th>
                <th scope="col">Lifted</th>
                <th scope="col">Personal best</th>
              </tr>
            </thead>
            <tbody>
              {logs.length ? (
                logs.map((l, i) => {
                  const label = records.get(norm(l.id));
                  return (
                    <tr key={i}>
                      <td className="ath-set-n num">{(l.setIndex ?? 0) + 1}</td>
                      <td className="num">{l.skipped ? <Status kind="skipped" /> : setText(l, e)}</td>
                      <td className="ath-set-pb">{label ? <RecordLabel text={recordText(label, l, e)} /> : null}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="k-muted" colSpan={3}>
                    No sets logged
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {(exNotes.get(keyOf(e)) ?? []).map((t, i) => (
            <p className="ath-ex-note" key={i}>
              {t}
            </p>
          ))}
        </div>
      );
    }),
  );
  const note = typeof s.note === "string" ? s.note.trim() : "";
  const told = s.body && typeof s.body.note === "string" ? s.body.note.trim() : "";
  return (
    <div className="ath-history-body">
      {note ? <p className="ath-quote">“{note}”</p> : null}
      {told ? <p className="ath-quote">Sore or hurt: “{told}”</p> : null}
      {lifts.length ? <div className="ath-lifts">{lifts}</div> : <p className="k-muted">No lifts in this workout.</p>}
    </div>
  );
}

function RecordLabel({ text }: { text: string }) {
  return (
    <span className="k-pb">
      <svg viewBox="0 0 12 12" aria-hidden="true">
        <path fill="currentColor" d="M3 1.5h6v1.2h1.8v1.1c0 1.3-.9 2.3-2.1 2.5A3 3 0 0 1 6.6 8v1.3H8v1.2H4V9.3h1.4V8a3 3 0 0 1-2.1-1.7C2.1 6.1 1.2 5.1 1.2 3.8V2.7H3V1.5Zm-.6 2.3c0 .6.3 1 .7 1.2V3.8h-.7Zm7.2 0h-.7V5c.4-.2.7-.6.7-1.2Z" />
      </svg>
      <span aria-hidden="true">{text}</span>
      <span className="sr">personal best: {text}</span>
    </span>
  );
}

/** Their running (§19.5): this week's metres by type, twelve weeks of them, and each run's bests. */
function Running({ athleteID }: { athleteID: string }) {
  const model = useModel();
  const history = useResource(historyKey(athleteID), () => loadHistory(athleteID));
  const workouts = history.value?.workouts ?? [];
  if (!hasRuns(workouts)) return null;
  const mondays = lastMondays(model.today);
  const weeks = weeklyMetres(workouts, mondays);
  const now = weeks[weeks.length - 1]!;
  const bests = runBests(workouts);
  const max = Math.max(...weeks.map((w) => w.total), 1);
  const W = 600;
  const H = 72;
  const gap = 6;
  const bar = (W - gap * (weeks.length - 1)) / weeks.length;
  const y = (v: number) => H - (v / max) * (H - 4);
  const columns: Column<RunBest>[] = [
    { key: "name", label: "Run", wrap: true, render: (b) => b.name },
    { key: "distance", label: "Distance", render: (b) => <span className="num">{runDistanceText(b.meters, b.kilograms)}</span> },
    {
      key: "fastest",
      label: "Fastest",
      align: "end",
      render: (b) => (b.fastest ? <span className="num">{raceTime(b.fastest.time)}</span> : <span className="k-muted">—</span>),
    },
    { key: "fastestOn", label: "Fastest, when", hideLabel: true, render: (b) => (b.fastest ? <span className="num k-muted">{dayShort(b.fastest.date)}</span> : null) },
    {
      key: "speed",
      label: "Top speed",
      align: "end",
      render: (b) => (b.topSpeed ? <span className="num">{speedText(b.topSpeed.speed)}</span> : <span className="k-muted">—</span>),
    },
    { key: "speedOn", label: "Top speed, when", hideLabel: true, render: (b) => (b.topSpeed ? <span className="num k-muted">{dayShort(b.topSpeed.date)}</span> : null) },
  ];
  return (
    <Section id="ath-running" hid="ath-running-h" title="Running" note="Last 12 weeks">
      <div className="ath-chart-keys" aria-hidden="true">
        {RUN_TYPES.filter((t) => weeks.some((w) => (w.byType[t] ?? 0) > 0)).map((t) => (
          <span key={t} className="ath-key">
            <RunSwatch type={t} />
            {RUN_TYPE_LABEL[t]}
            {now.byType[t] ? <b className="num">{" " + metresText(now.byType[t]!)}</b> : null}
          </span>
        ))}
      </div>
        <WeekBars
          count={weeks.length}
          width={W}
          height={H}
          className="ath-run-bars"
          center={(i) => i * (bar + gap) + bar / 2}
          tip={(i) => {
            const w = weeks[i]!;
            return {
              top: y(w.total),
              body: (
                <>
                  <b className="num">{metresText(w.total)}</b>
                  {w.total ? <span className="num">{metresByTypeText(w.byType)}</span> : null}
                  <span>{i === weeks.length - 1 ? "This week" : "Week of " + monthDay(w.week)}</span>
                </>
              ),
            };
          }}
        >
          {(active) =>
            weeks.map((w, i) => (
              <g key={w.week} className={"ath-run-week" + (i === active ? " is-active" : "")}>
                {w.total === 0 ? (
                  <rect className="ath-bar is-pending" x={i * (bar + gap)} y={H - 2} width={bar} height={2} rx={1} />
                ) : (
                  metresStack(w.byType).map((seg, k, all) => {
                    const top = y(seg.from + seg.meters);
                    const bottom = y(seg.from);
                    return (
                      <rect
                        key={seg.type}
                        className={"ath-run-seg is-" + seg.type}
                        x={i * (bar + gap)}
                        y={top}
                        width={bar}
                        height={Math.max(bottom - top - (k < all.length - 1 ? 1 : 0), 1)}
                        rx={k === all.length - 1 ? 3 : 0}
                      />
                    );
                  })
                )}
              </g>
            ))
          }
        </WeekBars>
        <div className="ath-axis" aria-hidden="true">
          <span>{monthDay(mondays[0]!)}</span>
          <span>This week</span>
        </div>
        <div className="sr">
          <table>
            <caption>Metres by week</caption>
            <thead>
              <tr>
                <th scope="col">Week of</th>
                <th scope="col">Metres</th>
                <th scope="col">By type</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.week}>
                  <td>{monthDay(w.week)}</td>
                  <td>{metresText(w.total)}</td>
                  <td>{metresByTypeText(w.byType)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      <h3 className="ath-card-sub">Bests</h3>
      <div className="ath-card-bleed">
        <Table
          rows={bests}
          columns={columns}
          rowKey={(b) => b.name + "|" + b.meters + "|" + (b.kilograms ?? "")}
          label="Running bests"
          empty="No timed reps or top speeds yet."
        />
      </div>
    </Section>
  );
}

/** How hard their sessions were (§27.2): this week's session load and average, twelve weeks of load, and recent sore or hurt reports. */
function Effort({ athleteID }: { athleteID: string }) {
  const model = useModel();
  const history = useResource(historyKey(athleteID), () => loadHistory(athleteID));
  const workouts = history.value?.workouts ?? [];
  if (!hasEffort(workouts)) return null;
  const mondays = lastMondays(model.today);
  const weeks = weeklyEffort(workouts, mondays);
  const now = weeks[weeks.length - 1]!;
  const reports = recentReports(workouts, model.today);
  const max = Math.max(...weeks.map((w) => w.load ?? 0), 1);
  const W = 600;
  const H = 72;
  const gap = 6;
  const bar = (W - gap * (weeks.length - 1)) / weeks.length;
  const y = (v: number) => H - (v / max) * (H - 4);
  const weekName = (i: number) => (i === weeks.length - 1 ? "This week" : "Week of " + monthDay(weeks[i]!.week));
  const average = (w: WeekEffort) => (w.effort != null ? averageText(w.effort) + " · " + effortWord(Math.round(w.effort)) : "—");
  const columns: Column<(typeof reports)[number]>[] = [
    { key: "date", label: "Day", render: (r) => <span className="num">{dayShort(r.date)}</span> },
    { key: "report", label: "Report", wrap: true, render: (r) => <BodyLevel level={r.body.level} text={bodyText(r.body)} /> },
    { key: "note", label: "Their words", wrap: true, render: (r) => (r.body.note ? <span className="ath-quote">“{r.body.note}”</span> : <span className="k-muted">—</span>) },
    { key: "title", label: "Workout", wrap: true, render: (r) => r.title || <span className="k-muted">—</span> },
  ];
  return (
    <Section id="ath-effort" hid="ath-effort-h" title="Effort" note="Last 12 weeks">
      <div className="ath-chart-keys" aria-hidden="true">
        <span className="ath-key">
          <span className="ath-key-swatch is-lead" />
          This week <b className="num">{now.load != null ? effortLoadText(now.load) : "—"}</b>
        </span>
        <span className="ath-key">
          Average effort <b className="num">{average(now)}</b>
        </span>
      </div>
      <p className="sr">
        This week: session load {now.load != null ? effortLoadText(now.load) : "none reported"}, average effort {average(now)}.
      </p>
      <WeekBars
        count={weeks.length}
        width={W}
        height={H}
        className="ath-run-bars"
        center={(i) => i * (bar + gap) + bar / 2}
        tip={(i) => {
          const w = weeks[i]!;
          return {
            top: w.load != null ? y(w.load) : H - 2,
            body:
              w.load != null ? (
                <>
                  <b className="num">{effortLoadText(w.load)}</b>
                  <span className="num">
                    Average {averageText(w.effort!)} · {plural(w.sessions, "session", "sessions")}
                  </span>
                  <span>{weekName(i)}</span>
                </>
              ) : (
                <>
                  <b>No effort reported</b>
                  <span>{weekName(i)}</span>
                </>
              ),
          };
        }}
      >
        {(active) =>
          weeks.map((w, i) =>
            w.load == null ? null : (
              <rect
                key={w.week}
                className={"ath-bar" + (i === weeks.length - 1 ? " is-lead" : "") + (i === active ? " is-active" : "")}
                x={i * (bar + gap)}
                y={y(w.load)}
                width={bar}
                height={Math.max(H - y(w.load), 2)}
                rx={3}
              />
            ),
          )
        }
      </WeekBars>
      <div className="ath-axis" aria-hidden="true">
        <span>{monthDay(mondays[0]!)}</span>
        <span>This week</span>
      </div>
      <div className="sr">
        <table>
          <caption>Session load by week</caption>
          <thead>
            <tr>
              <th scope="col">Week of</th>
              <th scope="col">Session load</th>
              <th scope="col">Average effort</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.week}>
                <td>{monthDay(w.week)}</td>
                <td>{w.load != null ? effortLoadText(w.load) : "None reported"}</td>
                <td>{w.effort != null ? averageText(w.effort) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ath-card-note">Session load is how hard (1–10) × minutes. A workout under 10 minutes or over 4 hours from start to finish shows its effort but adds no load.</p>
      <h3 className="ath-card-sub">Sore or hurt, last 4 weeks</h3>
      {reports.length ? (
        <div className="ath-card-bleed">
          <Table rows={reports} columns={columns} rowKey={(r) => r.date + bodyText(r.body)} label="Sore or hurt, last 4 weeks" empty="" />
        </div>
      ) : (
        <Notice>Nothing sore or hurt reported.</Notice>
      )}
    </Section>
  );
}

function RunSwatch({ type }: { type: RunType }) {
  return <span className={"ath-swatch is-" + type} aria-hidden="true" />;
}

/** Twelve weeks of bars; pointing at one keeps it bright, dims the rest and shows its numbers. */
function WeekBars({
  count,
  width,
  height,
  className,
  center,
  tip,
  children,
}: {
  count: number;
  width: number;
  height: number;
  className?: string;
  center: (i: number) => number;
  tip: (i: number) => { top: number; body: ReactNode };
  children: (active: number | null) => ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);
  const at = (clientX: number) => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect || !rect.width) return null;
    return Math.max(0, Math.min(count - 1, Math.floor(((clientX - rect.left) / rect.width) * count)));
  };
  const shown = active == null ? null : tip(active);
  const edge = active == null ? "" : active < 2 ? " is-start" : active > count - 3 ? " is-end" : "";
  return (
    <div ref={box} className="ath-bars-box" onPointerMove={(e) => setActive(at(e.clientX))} onPointerDown={(e) => setActive(at(e.clientX))} onPointerLeave={() => setActive(null)}>
      <svg className={"ath-bars" + (className ? " " + className : "") + (active != null ? " has-active" : "")} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true" focusable="false">
        {children(active)}
      </svg>
      {shown && active != null ? (
        <div className={"ath-tip ath-bar-tip" + edge} style={{ left: `${(center(active) / width) * 100}%`, top: `${(shown.top / height) * 100}%` }} aria-hidden="true">
          {shown.body}
        </div>
      ) : null}
    </div>
  );
}

type Range = "8" | "26" | "all";
const RANGES: { value: Range; label: string }[] = [
  { value: "8", label: "8 weeks" },
  { value: "26", label: "6 months" },
  { value: "all", label: "All" },
];

function Progress({ athlete }: { athlete: AthleteModel }) {
  const { ui, pickLift } = usePortal();
  const model = useModel();
  const [range, setRange] = useState<Range>("26");
  const history = useResource(historyKey(athlete.id), () => loadHistory(athlete.id));
  const prefs = usePrefs();
  const workouts = history.value?.workouts;
  const lifts = useMemo(() => (workouts ? progress(workouts) : undefined), [workouts, prefs.version]);
  const section = (children: ReactNode, note?: ReactNode, tools?: ReactNode) => (
    <Section id="progress" hid="ath-lifts-h" title="Lifts" note={note} tools={tools}>
      {children}
    </Section>
  );
  if (history.error) return section(<Notice alert>{errorMessage(history.error)}</Notice>);
  if (!lifts) return section(<Notice busy>Loading their history…</Notice>);
  if (!lifts.length) return section(<Notice>No weighed lifts shared yet. The chart shows the heaviest set of each week for a lift done with a weight.</Notice>);
  const byUse = [...lifts].sort((x, y) => y.points.length - x.points.length || (x.last < y.last ? 1 : -1));
  const chosen = lifts.find((l) => l.key === ui.lift[athlete.id]) ?? byUse[0]!;
  const from = range === "all" ? "" : addDays(mondayOf(model.today), -7 * (Number(range) - 1));
  const pts = chosen.points.filter((p) => p.week >= from);
  const change = pts.length > 1 ? pts[pts.length - 1]!.value - pts[0]!.value : 0;
  const changeText = pts.length > 1 ? signed(change) + " " + chosen.unit + " since " + monthDay(pts[0]!.week) : pts.length ? "One week in this range" : "Nothing in this range";
  const tools = (
    <>
      <label className="sr" htmlFor="ath-lift-select">
        Lift
      </label>
      <select id="ath-lift-select" className="k-input ath-lift-select" value={chosen.key} onChange={(e) => pickLift(athlete.id, e.target.value)}>
        {byUse.map((l) => (
          <option key={l.key} value={l.key}>
            {l.name}
          </option>
        ))}
      </select>
      <div className="bp-segmented ath-range" role="radiogroup" aria-label="Range">
        {RANGES.map((r) => (
          <button key={r.value} type="button" role="radio" aria-checked={range === r.value} className={"bp-segment" + (range === r.value ? " is-on" : "")} onClick={() => setRange(r.value)}>
            {r.label}
          </button>
        ))}
      </div>
    </>
  );
  return section(
    <>
      {pts.length ? <Chart points={pts} unit={chosen.unit} name={chosen.name} /> : <Notice>No {chosen.name.toLowerCase()} in this range.</Notice>}
      <ChartTable points={pts} lift={chosen} />
      <p className="ath-card-note">Heaviest set of each week. Point at a week for its set; click it to open that workout in History.</p>
    </>,
    chosen.name + " · " + changeText,
    tools,
  );
}

/** Opens a workout in History by its day, from the chart. */
const OPEN_WORKOUT = "ath-open-workout";

/** The width the chart is shown at, so its labels stay at text size. */
function useWidth(ref: RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth || null);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

function Chart({ points, unit, name }: { points: ProgressPoint[]; unit: string; name: string }) {
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box);
  const g = chartGeometry(points, unit, Math.max(320, width ?? 640));
  const [active, setActive] = useState<number | null>(null);
  const point = active != null ? points[active] : undefined;
  const dot = active != null ? g.dots[active] : undefined;
  const open = (i: number) => {
    const date = points[i]?.date;
    if (date) window.dispatchEvent(new CustomEvent(OPEN_WORKOUT, { detail: date }));
  };
  const nearest = (clientX: number) => {
    const rect = box.current?.querySelector("svg")?.getBoundingClientRect();
    if (!rect) return null;
    const x = ((clientX - rect.left) / rect.width) * g.w;
    let best = 0;
    g.dots.forEach((d, i) => {
      if (Math.abs(d.x - x) < Math.abs(g.dots[best]!.x - x)) best = i;
    });
    return best;
  };
  const describe = (p: ProgressPoint) => `Week of ${monthDay(p.week)}: ${weightNumber(p.value)} ${unit}${p.reps ? " × " + p.reps : ""}${p.date ? ", " + dayShort(p.date) : ""}`;
  return (
    <div
      ref={box}
      className="ath-chart-box"
      tabIndex={0}
      role="group"
      aria-label={`${name} by week. Arrow keys move between weeks; Enter opens that workout.`}
      onPointerMove={(e) => setActive(nearest(e.clientX))}
      onPointerLeave={() => setActive(null)}
      onClick={(e) => {
        const i = nearest(e.clientX);
        if (i != null) open(i);
      }}
      onFocus={() => setActive((a) => a ?? points.length - 1)}
      onBlur={() => setActive(null)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          e.preventDefault();
          setActive((a) => Math.max(0, Math.min(points.length - 1, (a ?? points.length - 1) + (e.key === "ArrowRight" ? 1 : -1))));
        } else if (e.key === "Home" || e.key === "End") {
          e.preventDefault();
          setActive(e.key === "Home" ? 0 : points.length - 1);
        } else if (e.key === "Enter" && active != null) {
          e.preventDefault();
          open(active);
        }
      }}
    >
      <svg viewBox={"0 0 " + g.w + " " + g.h} width={g.w} height={g.h} aria-hidden="true" focusable="false">
        {g.grid.map((line, i) => (
          <g key={"y" + i}>
            <line className="grid-line" x1={g.padL} x2={g.w - g.padR} y1={line.y} y2={line.y} />
            <text className="axis" x={g.padL - 8} y={Number((line.y + 4).toFixed(1))} textAnchor="end">
              {line.label}
            </text>
          </g>
        ))}
        {g.ticks.map((t, i) => (
          <text key={"x" + i} className="axis" x={t.x} y={g.h - 10} textAnchor="middle">
            {t.label}
          </text>
        ))}
        {g.area ? <path className="area" d={g.area} /> : null}
        {g.line ? <path className="line" d={g.line} /> : null}
        {dot ? <line className="ath-guide" x1={dot.x} x2={dot.x} y1={18} y2={g.h - g.padB} /> : null}
        {g.dots.map((d, i) => (
          <circle key={"d" + i} className={"dot" + (d.last ? " last" : "") + (i === active ? " is-active" : "")} cx={d.x} cy={d.y} r={i === active ? 5.5 : d.last ? 4.5 : 2.5} />
        ))}
        {active == null ? (
          <text className="value" x={g.value.x} y={g.value.y} textAnchor={g.value.anchor}>
            {g.value.text}
          </text>
        ) : null}
      </svg>
      {point && dot ? (
        <div className="ath-tip" style={{ left: `${(dot.x / g.w) * 100}%`, top: `${(dot.y / g.h) * 100}%` }} aria-hidden="true">
          <b className="num">
            {weightNumber(point.value)} {unit}
            {point.reps ? " × " + point.reps : ""}
          </b>
          <span>{point.date ? dayShort(point.date) : "Week of " + monthDay(point.week)}</span>
        </div>
      ) : null}
      <span className="sr" aria-live="polite">
        {point ? describe(point) : ""}
      </span>
    </div>
  );
}

function ChartTable({ points, lift }: { points: ProgressPoint[]; lift: LiftProgress }) {
  return (
    <div className="sr">
      <table>
        <caption>{lift.name}, heaviest set by week</caption>
        <thead>
          <tr>
            <th scope="col">Week of</th>
            <th scope="col">Heaviest</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.week}>
              <td>{monthDay(p.week)}</td>
              <td>{weightNumber(p.value) + " " + lift.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
